import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import HistoryIcon from "@mui/icons-material/History";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createSpaClientAlert,
  getSpaClientAlerts,
  getSpaClientProfile,
  updateSpaClientAlert,
  updateSpaClientProfile,
} from "../APIs/SpaAPI";
import SidebarLayout from "../components/sidebarLayout";
import SpaCommunicationPreferences from "../components/SpaCommunicationPreferences";
import { spaProfileAccess, visibleSpaProfile } from "../utils/spaProfilePermissions";
import { usePermissions } from "../contexts/PermissionsContext";
import { useSnackbar } from "../contexts/SnackBarContext";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const alertTypes = ["CANCELLATION", "NO_SHOW", "PAYMENT", "BEHAVIOR"];
const alertSeverities = ["INFO", "WARNING", "CRITICAL"];

const alertSeverityMap = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "error",
};

const statusColor = {
  Completed: "#28745C",
  COMPLETED: "#28745C",
  "No-show": "#B42318",
  NO_SHOW: "#B42318",
  Cancelled: "#7A4D00",
  CANCELLED: "#7A4D00",
};

const defaultAlertDraft = {
  type: "CANCELLATION",
  severity: "WARNING",
  message: "",
  visible: true,
};

const isPositiveInteger = (value) => /^\d+$/.test(String(value || "").trim());

const personName = (person, fallback = "-") => {
  const fullName = [person?.name, person?.surname].filter(Boolean).join(" ").trim();
  return fullName || person?.email || fallback;
};

const initialsFor = (name) => (
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "SP"
);

const formatDateTime = (value, language = 'en') => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const splitTextList = (value) => (
  String(value || "")
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
);

const splitIdList = (value) => (
  String(value || "")
    .split(/\n|,|\s/)
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0)
);

const idsFrom = (items = []) => items.map((item) => item.id).filter(Boolean).join(", ");
const linesFrom = (items = []) => items.join("\n");

function Section({ title, icon, count, action, children }) {
  return (
    <Paper sx={{ p: 2.25, height: "100%", borderRadius: 2 }}>
      <Stack spacing={1.75}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {icon}
            <Typography variant="h6" fontWeight={850}>
              {title}
            </Typography>
            {typeof count !== "undefined" && <Chip size="small" label={count} />}
          </Stack>
          {action}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function EmptyState({ text }) {
  return (
    <Typography sx={{ py: 2 }} color="text.secondary">
      {text}
    </Typography>
  );
}

function DetailChip({ icon, label }) {
  if (!label) return null;

  return (
    <Chip
      icon={icon}
      label={label}
      sx={{
        justifyContent: "flex-start",
        maxWidth: "100%",
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  );
}

function buildProfileForm(profile) {
  return {
    preferredOperatorIds: idsFrom(profile?.preferredOperators),
    preferredTreatmentIds: idsFrom(profile?.preferredTreatments),
    preferredCabinIds: idsFrom(profile?.preferredCabins),
    allergies: linesFrom(profile?.allergies),
    limitations: linesFrom(profile?.limitations),
    internalNotes: profile?.internalNotes || "",
  };
}

export function profilePayload(form, canManageSensitive = false) {
  return {
    preferredOperatorIds: splitIdList(form.preferredOperatorIds),
    preferredTreatmentIds: splitIdList(form.preferredTreatmentIds),
    preferredCabinIds: splitIdList(form.preferredCabinIds),
    ...(canManageSensitive ? {
      allergies: splitTextList(form.allergies),
      limitations: splitTextList(form.limitations),
      internalNotes: form.internalNotes.trim() || null,
    } : {}),
  };
}

function profileCompleteness(profile) {
  if (!profile) return 0;
  const checks = [
    profile.client?.email,
    profile.client?.phone,
    profile.preferredOperators?.length,
    profile.preferredTreatments?.length,
    profile.preferredCabins?.length,
    profile.allergies?.length || profile.limitations?.length,
    profile.internalNotes,
    profile.treatmentHistory?.length,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function SpaClientProfilePage() {
  const { t, i18n } = useTranslation();
  const p = (key, options) => t(`spa.clientProfile.${key}`, options);
  const dateTime = (value) => formatDateTime(value, i18n.resolvedLanguage || i18n.language);
  const navigate = useNavigate();
  const { id } = useParams();
  const { can, loading: permissionsLoading } = usePermissions();
  const { showSnackbar } = useSnackbar();
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const clientId = isPositiveInteger(id) ? Number(id) : null;
  const access = spaProfileAccess((permission) => !permissionsLoading && can(permission));
  const canManageClients = access.manage;
  const canViewProfile = access.view;
  const canViewSensitive = access.viewSensitive;
  const canManageSensitive = access.manageSensitive;

  const [lookupId, setLookupId] = useState("");
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(buildProfileForm(null));
  const [alerts, setAlerts] = useState([]);
  const [alertFilters, setAlertFilters] = useState({ type: "", severity: "", visible: "true" });
  const [loading, setLoading] = useState(Boolean(clientId));
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  const [error, setError] = useState("");
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [alertDraft, setAlertDraft] = useState(defaultAlertDraft);

  const loadProfile = useCallback(async () => {
    if (!clientId || !token || !tenant || !canViewProfile) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSpaClientProfile(clientId, token, tenant);
      const visible = visibleSpaProfile(data, canViewSensitive);
      setProfile(visible);
      setForm(buildProfileForm(visible));
    } catch (err) {
      setProfile(null);
      setError(localizedErrorMessage(err, i18n.t('spa.clientProfile.loadError')));
    } finally {
      setLoading(false);
    }
  }, [clientId, tenant, token, canViewProfile, canViewSensitive, i18n]);

  const loadAlerts = useCallback(async () => {
    if (!clientId || !token || !tenant || !canViewProfile) return;
    setAlertsLoading(true);
    try {
      const filters = {
        type: alertFilters.type || undefined,
        severity: alertFilters.severity || undefined,
        visible: alertFilters.visible || undefined,
      };
      setAlerts(await getSpaClientAlerts(clientId, filters, token, tenant));
    } catch (err) {
      showSnackbar(err?.message || i18n.t('spa.clientProfile.alertLoadError'), "error");
    } finally {
      setAlertsLoading(false);
    }
  }, [alertFilters, clientId, showSnackbar, tenant, token, canViewProfile, i18n]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const clientName = personName(profile?.client, clientId ? p('clientName', { id: clientId }) : p('title'));
  const completeness = profileCompleteness(visibleSpaProfile(profile, canViewSensitive));
  const history = profile?.treatmentHistory || [];
  const completedCount = history.length;
  const activeAlertsCount = alerts.filter((alert) => alert.visible).length;

  const preferenceRows = useMemo(() => [
    {
      label: t('spa.clientProfile.operators'),
      values: (profile?.preferredOperators || []).map((item) => personName(item, `${t('spa.fields.operator')} ${item.id}`)),
    },
    {
      label: t('spa.clientProfile.treatments'),
      values: (profile?.preferredTreatments || []).map((item) => item.name || `${t('spa.fields.treatment')} ${item.id}`),
    },
    {
      label: t('spa.clientProfile.cabins'),
      values: (profile?.preferredCabins || []).map((item) => item.name || `${t('spa.fields.cabin')} ${item.id}`),
    },
  ], [profile, t]);

  const openCreateAlert = () => {
    setEditingAlert(null);
    setAlertDraft(defaultAlertDraft);
    setAlertDialogOpen(true);
  };

  const openEditAlert = (alert) => {
    setEditingAlert(alert);
    setAlertDraft({
      type: alert.type,
      severity: alert.severity,
      message: alert.message,
      visible: alert.visible,
    });
    setAlertDialogOpen(true);
  };

  const handleProfileSave = async () => {
    if (!clientId || !canManageClients) return;
    setSavingProfile(true);
    try {
      const updated = await updateSpaClientProfile(clientId, profilePayload(form, canManageSensitive), token, tenant);
      const visible = visibleSpaProfile(updated, canViewSensitive);
      setProfile(visible);
      setForm(buildProfileForm(visible));
      showSnackbar(p('saved'), "success");
    } catch (err) {
      showSnackbar(err?.message || p('saveError'), "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAlertSave = async () => {
    if (!clientId || !canManageClients || !alertDraft.message.trim()) return;
    setSavingAlert(true);
    try {
      if (editingAlert) {
        await updateSpaClientAlert(clientId, editingAlert.id, alertDraft, token, tenant);
        showSnackbar(p('alertSaved'), "success");
      } else {
        await createSpaClientAlert(clientId, alertDraft, token, tenant);
        showSnackbar(p('alertCreated'), "success");
      }
      setAlertDialogOpen(false);
      await loadAlerts();
    } catch (err) {
      showSnackbar(err?.message || p('alertSaveError'), "error");
    } finally {
      setSavingAlert(false);
    }
  };

  const handleHideAlert = async (alert) => {
    if (!clientId || !canManageClients) return;
    try {
      await updateSpaClientAlert(clientId, alert.id, { visible: false }, token, tenant);
      showSnackbar(p('alertHidden'), "success");
      await loadAlerts();
    } catch (err) {
      showSnackbar(err?.message || p('alertHideError'), "error");
    }
  };

  if (permissionsLoading) {
    return <SidebarLayout><Box sx={{ p: 3 }}><CircularProgress /></Box></SidebarLayout>;
  }
  if (!canViewProfile) {
    return <SidebarLayout><Box sx={{ p: 3 }}><Alert severity="warning">{t('spa.communications.noProfileAccess')}</Alert></Box></SidebarLayout>;
  }

  if (!clientId) {
    return (
      <SidebarLayout>
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760, mx: "auto" }}>
          <Stack spacing={2}>
            <Typography variant="h4" fontWeight={900}>
              {p('title')}
            </Typography>
            <Alert severity="info">
              {p('lookupHint')}
            </Alert>
            <Paper sx={{ p: 2.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label={p('clientId')}
                  value={lookupId}
                  onChange={(event) => setLookupId(event.target.value)}
                  inputProps={{ inputMode: "numeric" }}
                />
                <Button
                  variant="contained"
                  disabled={!isPositiveInteger(lookupId)}
                  onClick={() => navigate(`/spa/clients/${lookupId.trim()}`)}
                >
                  {p('openProfile')}
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </SidebarLayout>
    );
  }

  if (loading) {
    return (
      <SidebarLayout>
        <Box sx={{ p: 3, display: "grid", placeItems: "center", minHeight: "50vh" }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography>{t("spa.common.loading", { defaultValue: "Loading SPA client profile..." })}</Typography>
          </Stack>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: "auto" }}>
        <Stack spacing={2.5}>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: "#0B6E99", fontWeight: 900 }}>
                  {initialsFor(clientName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h4" fontWeight={900}>
                      {clientName}
                    </Typography>
                    <Chip size="small" label={`ID ${clientId}`} />
                    <Chip size="small" color="success" label={p('live')} />
                  </Stack>
                  <Typography color="text.secondary">
                    {t("spa.clientProfile.subtitle", {
                      defaultValue: "SPA history, preferences, alerts, allergies, limitations, and notes.",
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {canViewSensitive && <Button startIcon={<MedicalInformationIcon />} variant="outlined"
                  onClick={() => navigate(`/spa/clients/${clientId}/clinical`)}>{t('spa.clinical.open')}</Button>}
                <DetailChip icon={<MailOutlineIcon />} label={profile?.client?.email} />
                <DetailChip icon={<LocalPhoneIcon />} label={profile?.client?.phone} />
                <DetailChip icon={<HistoryIcon />} label={p('updated', { date: dateTime(profile?.updatedAt) })} />
              </Stack>
            </Stack>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {!canManageClients ? (
            <Alert severity="info">
              {t('spa.communications.readOnly')}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, minHeight: 120, borderTop: "4px solid #28745C" }}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  {p('completed')}
                </Typography>
                <Typography variant="h4" fontWeight={900}>
                  {completedCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, minHeight: 120, borderTop: "4px solid #B42318" }}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  {p('activeAlerts')}
                </Typography>
                <Typography variant="h4" fontWeight={900}>
                  {activeAlertsCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, minHeight: 120, borderTop: "4px solid #D47200" }}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  {p('completeness')}
                </Typography>
                <Typography variant="h4" fontWeight={900}>
                  {completeness}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={completeness}
                  sx={{ mt: 1, height: 8, borderRadius: 99 }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Section title={p('profile')} icon={<PersonIcon color="primary" />}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={p('operatorIds')}
                      value={form.preferredOperatorIds}
                      onChange={(event) => setForm((current) => ({ ...current, preferredOperatorIds: event.target.value }))}
                      disabled={!canManageClients || savingProfile}
                      helperText={p('idsHelp')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={p('treatmentIds')}
                      value={form.preferredTreatmentIds}
                      onChange={(event) => setForm((current) => ({ ...current, preferredTreatmentIds: event.target.value }))}
                      disabled={!canManageClients || savingProfile}
                      helperText={p('idsHelp')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={p('cabinIds')}
                      value={form.preferredCabinIds}
                      onChange={(event) => setForm((current) => ({ ...current, preferredCabinIds: event.target.value }))}
                      disabled={!canManageClients || savingProfile}
                      helperText={p('idsHelp')}
                    />
                  </Grid>
                  {canViewSensitive && <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label={p('allergies')}
                      value={form.allergies}
                      onChange={(event) => setForm((current) => ({ ...current, allergies: event.target.value }))}
                      disabled={!canManageSensitive || savingProfile}
                      helperText={p('linesHelp')}
                    />
                  </Grid>}
                  {canViewSensitive && <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label={p('limitations')}
                      value={form.limitations}
                      onChange={(event) => setForm((current) => ({ ...current, limitations: event.target.value }))}
                      disabled={!canManageSensitive || savingProfile}
                      helperText={p('linesHelp')}
                    />
                  </Grid>}
                  {canViewSensitive && <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label={p('internalNotes')}
                      value={form.internalNotes}
                      onChange={(event) => setForm((current) => ({ ...current, internalNotes: event.target.value }))}
                      disabled={!canManageSensitive || savingProfile}
                    />
                  </Grid>}
                </Grid>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={savingProfile ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={!canManageClients || savingProfile}
                    onClick={handleProfileSave}
                  >
                    {savingProfile ? t('spa.common.saving') : p('save')}
                  </Button>
                </Stack>
              </Section>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Section title={p('preferences')} icon={<FavoriteBorderIcon color="success" />}>
                <Stack spacing={1}>
                  {preferenceRows.map((row) => (
                    <Paper key={row.label} variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography fontWeight={850}>
                        {row.values.length ? row.values.join(", ") : "-"}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Section>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Section title={p('history')} icon={<HistoryIcon color="primary" />} count={history.length}>
                {history.length === 0 ? (
                  <EmptyState text={p('emptyHistory')} />
                ) : (
                  <Stack spacing={1}>
                    {history.map((visit) => {
                      const color = statusColor.COMPLETED;
                      return (
                        <Paper
                          key={`${visit.appointmentId}-${visit.completedAt || visit.actualStartAt}`}
                          variant="outlined"
                          sx={{ p: 1.5, borderLeft: `5px solid ${color}` }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <Typography fontWeight={850}>{dateTime(visit.plannedStartAt)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p('appointment', { id: visit.appointmentId })}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography fontWeight={850}>{visit.actualTreatment?.name || "-"}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('spa.fields.treatment')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography>{personName(visit.responsibleOperator, "-")}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p('responsibleOperator')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Chip
                                size="small"
                                label={t('spa.statuses.COMPLETED')}
                                sx={{
                                  color,
                                  backgroundColor: alpha(color, 0.1),
                                  border: `1px solid ${alpha(color, 0.28)}`,
                                  fontWeight: 800,
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Section>
            </Grid>

            {canViewSensitive && <Grid item xs={12} lg={4}>
              <Section
                title={p('health')}
                icon={<MedicalInformationIcon color="error" />}
                count={(profile?.allergies || []).length + (profile?.limitations || []).length}
              >
                {(profile?.allergies || []).length === 0 && (profile?.limitations || []).length === 0 ? (
                  <EmptyState text={p('emptyHealth')} />
                ) : (
                  <Stack spacing={1}>
                    {(profile?.allergies || []).map((item) => (
                      <Chip key={`allergy-${item}`} label={p('allergy', { value: item })} color="error" variant="outlined" sx={{ justifyContent: "flex-start", height: "auto", py: 0.75 }} />
                    ))}
                    {(profile?.limitations || []).map((item) => (
                      <Chip key={`limitation-${item}`} label={p('limitation', { value: item })} color="warning" variant="outlined" sx={{ justifyContent: "flex-start", height: "auto", py: 0.75 }} />
                    ))}
                  </Stack>
                )}
              </Section>
            </Grid>}
          </Grid>

          {!canViewSensitive && <Alert severity="info">{t('spa.communications.restricted')}</Alert>}
          {profile && <SpaCommunicationPreferences key={`${tenant}:${token}:${clientId}`} clientId={clientId} token={token} tenant={tenant} />}

          <Section
            title={p('alerts')}
            icon={<WarningAmberIcon color="warning" />}
            count={alerts.length}
            action={(
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!canManageClients}
                onClick={openCreateAlert}
              >
                {p('newAlert')}
              </Button>
            )}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="alert-type-filter">{p('type')}</InputLabel>
                  <Select
                    labelId="alert-type-filter"
                    value={alertFilters.type}
                    label={p('type')}
                    onChange={(event) => setAlertFilters((current) => ({ ...current, type: event.target.value }))}
                  >
                    <MenuItem value="">{p('allTypes')}</MenuItem>
                    {alertTypes.map((type) => <MenuItem key={type} value={type}>{t(`spa.daily.alertTypes.${type}`)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="alert-severity-filter">{p('severity')}</InputLabel>
                  <Select
                    labelId="alert-severity-filter"
                    value={alertFilters.severity}
                    label={p('severity')}
                    onChange={(event) => setAlertFilters((current) => ({ ...current, severity: event.target.value }))}
                  >
                    <MenuItem value="">{p('allSeverities')}</MenuItem>
                    {alertSeverities.map((severity) => <MenuItem key={severity} value={severity}>{p(`severities.${severity}`)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="alert-visible-filter">{p('visibility')}</InputLabel>
                  <Select
                    labelId="alert-visible-filter"
                    value={alertFilters.visible}
                    label={p('visibility')}
                    onChange={(event) => setAlertFilters((current) => ({ ...current, visible: event.target.value }))}
                  >
                    <MenuItem value="">{t('spa.common.all')}</MenuItem>
                    <MenuItem value="true">{p('visible')}</MenuItem>
                    <MenuItem value="false">{p('hidden')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {alertsLoading ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">{p('loadingAlerts')}</Typography>
              </Stack>
            ) : alerts.length === 0 ? (
              <EmptyState text={p('emptyAlerts')} />
            ) : (
              <Stack spacing={1}>
                {alerts.map((alert) => (
                  <Alert
                    key={alert.id}
                    severity={alertSeverityMap[alert.severity] || "info"}
                    sx={{
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      '& .MuiAlert-message': { minWidth: 0, flex: 1, overflowWrap: 'anywhere' },
                      '& .MuiAlert-action': {
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: 'flex-end',
                        pl: { xs: 0, sm: 2 },
                        ml: { xs: 0, sm: 'auto' },
                        mr: { xs: 0, sm: -1 },
                      },
                    }}
                    action={canManageClients ? (
                      <Stack direction="row" spacing={0.5}>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditAlert(alert)}>
                          {p('edit')}
                        </Button>
                        {alert.visible ? (
                          <Button size="small" color="inherit" startIcon={<VisibilityOffIcon />} onClick={() => handleHideAlert(alert)}>
                            {p('hide')}
                          </Button>
                        ) : null}
                      </Stack>
                    ) : null}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Typography fontWeight={850}>{t(`spa.daily.alertTypes.${alert.type}`)}</Typography>
                        <Chip size="small" label={p(`severities.${alert.severity}`)} />
                        {!alert.visible ? <Chip size="small" label={p('hidden')} /> : null}
                      </Stack>
                      <Typography variant="body2">{alert.message}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p('alertUpdated', { date: dateTime(alert.updatedAt), count: alert.history?.length || 0 })}
                      </Typography>
                    </Stack>
                  </Alert>
                ))}
              </Stack>
            )}
          </Section>
        </Stack>
      </Box>

      <Dialog open={alertDialogOpen} onClose={() => setAlertDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingAlert ? p('editAlert') : p('createAlert')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="alert-type-label">{p('type')}</InputLabel>
                  <Select
                    labelId="alert-type-label"
                    value={alertDraft.type}
                    label={p('type')}
                    onChange={(event) => setAlertDraft((current) => ({ ...current, type: event.target.value }))}
                  >
                    {alertTypes.map((type) => <MenuItem key={type} value={type}>{t(`spa.daily.alertTypes.${type}`)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="alert-severity-label">{p('severity')}</InputLabel>
                  <Select
                    labelId="alert-severity-label"
                    value={alertDraft.severity}
                    label={p('severity')}
                    onChange={(event) => setAlertDraft((current) => ({ ...current, severity: event.target.value }))}
                  >
                    {alertSeverities.map((severity) => <MenuItem key={severity} value={severity}>{p(`severities.${severity}`)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label={p('message')}
              value={alertDraft.message}
              onChange={(event) => setAlertDraft((current) => ({ ...current, message: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel id="alert-visible-label">{p('visible')}</InputLabel>
              <Select
                labelId="alert-visible-label"
                value={String(alertDraft.visible)}
                label={p('visible')}
                onChange={(event) => setAlertDraft((current) => ({ ...current, visible: event.target.value === "true" }))}
              >
                <MenuItem value="true">{p('visible')}</MenuItem>
                <MenuItem value="false">{p('hidden')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialogOpen(false)} disabled={savingAlert}>
            {t('spa.common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={savingAlert || !alertDraft.message.trim()}
            onClick={handleAlertSave}
            startIcon={savingAlert ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          >
            {savingAlert ? t('spa.common.saving') : p('saveAlert')}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
}

export default SpaClientProfilePage;
